<template>
  <ion-app>
    <!-- Fullscreen Toggle -->
    <ion-button class="fullscreen-toggle" @click="toggleFullscreen" fill="clear">
      <ion-icon :icon="isFullscreen ? contractOutline : expandOutline" slot="icon-only"></ion-icon>
    </ion-button>

    <ChapterListModal
      :is-open="isChapterListOpen"
      @close="isChapterListOpen = false"
      @select-chapter="onSelectChapter"
    />
    <BattleListModal
      v-if="selectedCampaign"
      :is-open="isBattleListOpen"
      :campaign="selectedCampaign"
      @close="selectedCampaign = null"
      @back="selectedCampaign = null; isChapterListOpen = true"
      @select-level="playLevel"
      @claim-reward="onClaimReward"
    />
    <RewardModal
      :is-open="!!rewardToShow"
      :reward="rewardToShow?.reward"
      :campaign-name="rewardToShow?.campaignName"
      @accept="onRewardAccepted"
    />
    <LevelPlayChoiceModal :is-open="isPlayChoiceOpen" :level-key="playChoiceLevelKey"
      @play="onPlayChoicePlay" @replay-solution="onPlayChoiceReplay" @close="onPlayChoiceBack" />
    <LevelWinModal :is-open="game.state.screen === 'level_win'" @continue="continueToNextLevel"
      @claim-reward="onWinClaimReward" @replay="replayCurrentLevel" @replaySolution="replaySolution" @home="returnToHome" />
    <CandidatesBrowserModal v-if="isDev" :is-open="isCandidatesBrowserOpen" @close="isCandidatesBrowserOpen = false" />
    <ion-modal :is-open="isResetConfirmOpen" :backdrop-dismiss="false" class="card-modal">
      <ion-card>
        <ion-card-header>
          <ion-card-title>Reset Progress</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p>Are you sure you want to delete all your progress? This cannot be undone!</p>
          <ion-button @click="confirmReset" expand="block" color="danger">Reset</ion-button>
          <ion-button @click="isResetConfirmOpen = false" expand="block" color="light">Cancel</ion-button>
        </ion-card-content>
      </ion-card>
    </ion-modal>
    <ion-modal :is-open="isAboutOpen" @didDismiss="isAboutOpen = false" class="card-modal">
      <ion-card>
        <ion-card-header>
          <ion-card-title>About Hexwood</ion-card-title>
        </ion-card-header>
        <ion-card-content class="about-content">
          <p>A turn-based tactics game that feels like real-time chaos.</p>
          <p>Every creature acts simultaneously. When you move, your enemies move too. To survive, you'll need to think one step ahead: <em>where will they be</em> when you get there?</p>
          <p>There's no attack button. Move into an enemy to strike. But if they're moving toward you at the same time... only one of you wins that exchange.</p>
          <p>Welcome to Hexwood.</p>
          <ion-button @click="isAboutOpen = false" expand="block" color="light">Close</ion-button>
        </ion-card-content>
      </ion-card>
    </ion-modal>
    <ion-content class="content-container">
      <!-- Home Screen -->
      <div v-if="game.state.screen === 'home'" class="screen-container">
        <ion-card>
          <ion-card-header>
            <ion-card-title>Hexwood</ion-card-title>
            <ion-card-subtitle>Ravenna's Curse</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content>
            <ion-button @click="isChapterListOpen = true" expand="block">Play</ion-button>

            <ion-button v-if="isDev" @click="playSandbox" expand="block" color="tertiary">Play Sandbox</ion-button>
            <ion-button v-if="isDev" @click="isCandidatesBrowserOpen = true" expand="block" color="tertiary">Curate
              Candidates</ion-button>
            <ion-button v-if="hasAnyProgress" @click="isResetConfirmOpen = true" expand="block"
              color="danger">Reset Progress</ion-button>
            <ion-button @click="isAboutOpen = true" expand="block" fill="clear">About</ion-button>
            <ion-button href="https://forms.gle/RRhCMTEDMGDCu5mQ7" target="_blank" expand="block" fill="clear">Feedback</ion-button>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Game Screen & Overlays -->
      <div v-show="game.state.screen !== 'home'">
        <PhaserGame v-if="game.state.screen !== 'home'" />
        <EnemyInfoBar />
        <LevelDisplay />
        <UndoButton />
        <ActionToolbar />
        <GameToolbar />
        <SolutionReplay v-if="isDev" />
        <CurationPanel v-if="isDev" />
        <MoveCounter />
        <SolutionReplayControls />

        <!-- Tutorial -->
        <TutorialModal :is-open="!!game.state.tutorial.currentTutorial" :text="game.state.tutorial.currentTutorial?.text"
          :image-url="game.state.tutorial.currentTutorial?.imageUrl" @close="dismissTutorial" />

        <!-- Win Screen -->
        <ion-modal :is-open="game.state.screen === 'win'" :backdrop-dismiss="false" class="card-modal">
          <ion-card>
            <ion-card-header>
              <ion-card-title>Game Complete!</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <p>Congratulations! You have beaten all available campaigns.</p>
              <ion-button @click="returnToHome" expand="block" color="light">Return to Home</ion-button>
            </ion-card-content>
          </ion-card>
        </ion-modal>

        <!-- Lose Screen -->
        <ion-modal :is-open="game.state.screen === 'lose'" :backdrop-dismiss="false" class="card-modal">
          <ion-card>
            <ion-card-header>
              <ion-card-title>Game Over</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-button @click="restart" expand="block">Try Again</ion-button>
              <ion-button @click="returnToHome" expand="block" color="light">Return to Home</ion-button>
            </ion-card-content>
          </ion-card>
        </ion-modal>
      </div>
    </ion-content>
  </ion-app>
</template>

<script setup>
import { inject, ref, computed } from 'vue';
import { IonApp, IonContent, IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent, IonButton, IonModal, IonIcon } from '@ionic/vue';
import { expandOutline, contractOutline } from 'ionicons/icons';
import { GameEvents } from '../lib/GameEvents';
import PhaserGame from './PhaserGame.vue';
import ActionToolbar from './ActionToolbar.vue';
import UndoButton from './UndoButton.vue';
import GameToolbar from './GameToolbar.vue';
import LevelDisplay from './LevelDisplay.vue';
import MoveCounter from './MoveCounter.vue';
import SolutionReplay from './components/dev/SolutionReplay.vue';
import CurationPanel from './components/dev/CurationPanel.vue';
import CandidatesBrowserModal from './components/dev/CandidatesBrowserModal.vue';
import LevelPlayChoiceModal from './LevelPlayChoiceModal.vue';
import LevelWinModal from './LevelWinModal.vue';
import ChapterListModal from './ChapterListModal.vue';
import BattleListModal from './BattleListModal.vue';
import RewardModal from './RewardModal.vue';
import TutorialModal from './TutorialModal.vue';
import campaignsData from '../../content/dist/campaigns.json';
import SolutionReplayControls from './components/SolutionReplayControls.vue';
import EnemyInfoBar from './components/EnemyInfoBar.vue';

const isDev = process.env.NODE_ENV === 'development';
const game = inject('game');
const isChapterListOpen = ref(false);
const selectedCampaign = ref(null);
const rewardToShow = ref(null);  // { reward, campaignId, campaignName } or null
const isBattleListOpen = computed(() => selectedCampaign.value !== null);
const isResetConfirmOpen = ref(false);
const isAboutOpen = ref(false);

const isFullscreen = ref(false);
const isCandidatesBrowserOpen = ref(false);
const playChoiceLevelKey = ref(null);
const isPlayChoiceOpen = computed(() => playChoiceLevelKey.value !== null);

const toggleFullscreen = async () => {
  if (!document.fullscreenElement) {
    await document.documentElement.requestFullscreen();
    isFullscreen.value = true;
  } else {
    await document.exitFullscreen();
    isFullscreen.value = false;
  }
};

// Sync state when user exits fullscreen via Escape key
document.addEventListener('fullscreenchange', () => {
  isFullscreen.value = !!document.fullscreenElement;
});

const hasAnyProgress = computed(() => game.state.progress.hasAnyProgress);

const continueToNextLevel = () => game.emit(GameEvents.CONTINUE_TO_NEXT_LEVEL);
const replayCurrentLevel = () => game.emit(GameEvents.REPLAY_CURRENT_LEVEL);
const replaySolution = () => game.emit(GameEvents.START_SOLUTION_REPLAY, { solution: game.state.gameplay.lastCompletedLevelSolution, source: 'win_screen' });
const dismissTutorial = () => game.emit(GameEvents.DISMISS_TUTORIAL);
const returnToHome = () => game.emit(GameEvents.RETURN_TO_HOME);
const playSandbox = () => game.emit(GameEvents.START_SANDBOX);
const playLevel = (levelId) => {
  const levelState = game.state.progress.levelStates[levelId];
  const isComplete = levelState && (levelState.bestPerfect !== null || levelState.bestAssisted !== null);
  const hasSavedSolution = levelState?.bestSolution?.solution;
  if (isComplete && hasSavedSolution) {
    playChoiceLevelKey.value = levelId;
  } else {
    selectedCampaign.value = null;
    game.emit(GameEvents.PLAY_LEVEL, { levelKey: levelId });
  }
};
const onSelectChapter = (campaign) => {
  isChapterListOpen.value = false;
  selectedCampaign.value = campaign;
};

const onWinClaimReward = () => {
  const pending = game.state.progress.completedChapterReward;
  if (!pending) return;
  rewardToShow.value = {
    reward: pending.reward,
    campaignId: pending.campaignId,
    campaignName: pending.campaignName,
  };
  // Dismiss the level_win screen
  game.emit(GameEvents.SET_SCREEN, 'home');
};

const onClaimReward = (campaignId) => {
  const campaign = campaignsData.campaigns.find(c => c.id === campaignId);
  if (!campaign?.reward) return;
  rewardToShow.value = {
    reward: campaign.reward,
    campaignId: campaign.id,
    campaignName: campaign.name,
  };
};

const onRewardAccepted = () => {
  const campaignId = rewardToShow.value.campaignId;
  game.emit(GameEvents.CLAIM_REWARD, { campaignId });
  rewardToShow.value = null;
  selectedCampaign.value = null;

  // Auto-navigate to next chapter's battle list
  const campaigns = campaignsData.campaigns;
  const currentIdx = campaigns.findIndex(c => c.id === campaignId);
  const nextCampaign = campaigns[currentIdx + 1];
  if (nextCampaign) {
    selectedCampaign.value = nextCampaign;
  }
};

const onPlayChoiceBack = () => {
  playChoiceLevelKey.value = null;
  // Don't reopen anything — the battle list is still open underneath
};
const onPlayChoicePlay = () => {
  selectedCampaign.value = null;
  game.emit(GameEvents.PLAY_LEVEL, { levelKey: playChoiceLevelKey.value });
  playChoiceLevelKey.value = null;
};
const onPlayChoiceReplay = () => {
  const levelKey = playChoiceLevelKey.value;
  const solution = game.state.progress.levelStates[levelKey]?.bestSolution?.solution;
  selectedCampaign.value = null;
  game.emit(GameEvents.START_SOLUTION_REPLAY, { levelKey, solution });
  playChoiceLevelKey.value = null;
};
const restart = () => game.dispatch(GameEvents.RESTART_GAME);
const confirmReset = () => {
  game.emit(GameEvents.RESET_PROGRESS);
  isResetConfirmOpen.value = false;
};
</script>

<style scoped>
.fullscreen-toggle {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  --color: var(--hw-text-muted);
  font-size: 20px;
}

.fullscreen-toggle:hover {
  --color: var(--hw-text);
}

.content-container {
  --background: var(--hw-bg-base);
}

.screen-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, var(--hw-bg-forest-light) 0%, var(--hw-bg-forest) 100%);
}

ion-card {
  --background: var(--hw-bg-surface);
  --color: var(--hw-text);
  text-align: center;
  padding: 20px;
  border-radius: var(--hw-radius);
  border: 5px solid #6b4226;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.5),
    inset 0 2px 0 rgba(0, 0, 0, 0.35),
    inset 0 -2px 0 rgba(210, 165, 100, 0.5),
    inset -2px 0 0 rgba(180, 130, 70, 0.2),
    inset 2px 0 0 rgba(0, 0, 0, 0.2);
  background:
    repeating-linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.04) 0px,
      transparent 1px,
      transparent 5px,
      rgba(0, 0, 0, 0.04) 6px
    ),
    linear-gradient(
      180deg,
      #4a3422 0%,
      var(--hw-bg-surface) 40%,
      #301c10 100%
    );
}

ion-card-title {
  font-family: var(--hw-font-display);
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 20px;
  color: var(--hw-accent);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

ion-card-subtitle {
  font-family: var(--hw-font-display);
  font-style: italic;
  color: var(--hw-text-muted);
  letter-spacing: 0.1em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.about-content p {
  margin-bottom: 1em;
  line-height: 1.5;
  max-width: 300px;
}

.about-content p:last-of-type {
  font-style: italic;
  margin-top: 1.5em;
}
</style>

<style>
/* Unscoped — global Hexwood overrides for Ionic components */
ion-modal.card-modal {
  --height: auto;
  --width: auto;
  --max-width: 90vw;
  --max-height: 90vh;
  --border-radius: var(--hw-radius);
}

ion-card {
  --background: var(--hw-bg-surface);
  --color: var(--hw-text);
  border-radius: var(--hw-radius);
  box-shadow: var(--hw-shadow);
}

ion-card-title {
  font-family: var(--hw-font-display);
  color: var(--hw-accent);
}

ion-button {
  --border-radius: var(--hw-radius);
  box-shadow: var(--hw-shadow);
}

/* Outline buttons — visible border, readable text */
ion-button[fill="outline"] {
  --border-width: 1px;
  --border-color: var(--hw-border-strong);
  --color: var(--hw-text);
  --background-hover: var(--hw-bg-elevated);
}

/* Clear buttons (About, Feedback, etc.) — subtle but legible */
ion-button[fill="clear"] {
  --color: var(--hw-text-muted);
  box-shadow: none;
}
ion-button[fill="clear"]:hover {
  --color: var(--hw-text);
}

ion-toolbar {
  --background: var(--hw-bg-base);
  --color: var(--hw-text);
  --border-color: var(--hw-border);
}
</style>
